import useStorage from "@/hooks/use-storage";
import { Column, ColumnKey } from "../shared/types";
import { fillColumnProps } from "../shared/utils";

type ColumnState = {
    show?: boolean
}

export interface useColumnsProps<T = any> {
    columns: (Column<T> | undefined | null | false)[],
    key: string
}

function getInitialColumnStates(initialColumns: Column<any>[]) {
    const res = {} as { [key: string]: ColumnState };

    for (const column of initialColumns) {
        const { show = true } = column;
        res[String(column.key)] = { show };
    }

    return res;
}

export default function useColumns<T = any>({
    columns: initialColumns,
    key: tableKey
}: useColumnsProps<T>) {
    const _initialColumns = initialColumns.filter(c => Boolean(c)).map(fillColumnProps);

    const [allColumnStates, setAllColumnState] = useStorage<{ [tableKey: string]: { [columnKey: string]: ColumnState } }>("column-states", "local", {});
    const _columnStates = { ...getInitialColumnStates(_initialColumns), ...allColumnStates[tableKey] };

    const columns = _initialColumns.map(column => ({ ...column, ..._columnStates[String(column.key)] }))

    const getColumn = (columnKey: ColumnKey) => {
        return columns.find(c => c.key === columnKey);
    }

    const setColumn = (columnKey: ColumnKey, newColumnData: Partial<Column<T>>) => {
        const key = String(columnKey);
        if (!(key in _columnStates)) return;
        const newColumnStates = { ..._columnStates, [key]: { ..._columnStates[key], ...newColumnData } };
        const newAllColumnStates = { ...allColumnStates, [tableKey]: newColumnStates };
        setAllColumnState(newAllColumnStates)
    }

    return { columns, getColumn, setColumn };
}

export type useColumnsReturn<T = any> = ReturnType<typeof useColumns<T>>;
